import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tyreManagementApi } from '../api/tyreManagementApi';
import { tyreQueryKeys } from '../api/queryKeys';

// One cached registry serves reports and analytics. Active jobs poll independently.
export default function useReportJobs(active) {
  const queryClient = useQueryClient();
  const jobsQuery = useQuery({
    queryKey: tyreQueryKeys.reportJobs(),
    queryFn: tyreManagementApi.getReportJobs,
    enabled: active,
    select: (payload) => payload?.results || payload || [],
  });
  const jobs = jobsQuery.data || [];
  const activeIds = jobs
    .filter((job) => ['queued', 'running'].includes(job.status))
    .map((job) => String(job.id))
    .sort();
  const upsertJob = useCallback((job) => {
    if (job?.id == null) return;
    queryClient.setQueryData(tyreQueryKeys.reportJobs(), (currentPayload) => {
      const current = currentPayload?.results || currentPayload || [];
      const next = [job, ...current.filter((item) => String(item.id) !== String(job.id))];
      return currentPayload?.results ? { ...currentPayload, results: next } : next;
    });
  }, [queryClient]);
  const statusQuery = useQuery({
    queryKey: tyreQueryKeys.reportJobStatuses(activeIds),
    queryFn: async () => {
      const results = await Promise.allSettled(
        activeIds.map((id) => tyreManagementApi.getReportJob(id))
      );
      results.forEach((result) => {
        if (result.status === 'fulfilled') upsertJob(result.value);
      });
      return results;
    },
    enabled: active && activeIds.length > 0,
    keepPreviousData: true,
    refetchInterval: 4000,
    refetchIntervalInBackground: false,
  });

  const hasStatusError = (statusQuery.data || [])
    .some((result) => result.status === 'rejected');
  const statusError = hasStatusError
    ? 'Report status could not be updated. Retrying automatically.'
    : '';
  return {
    jobs,
    jobsLoading: jobsQuery.isLoading,
    jobsError: jobsQuery.error?.message || statusError,
    loadJobs: jobsQuery.refetch,
    upsertJob,
  };
}
